import React, { useCallback, useEffect, useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const stamp = value => value ? new Date(value).toLocaleString('en-US', {timeZone:'America/Chicago', timeZoneName:'short'}) : 'Not recorded';
const label = value => typeof value === 'string' ? value.replaceAll('_', ' ') : 'Unknown';
const flag = value => value === true ? 'Enabled' : value === false ? 'Disabled' : 'Unknown';

export default function NinjaEventHealth() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { setHealth((await api.get('/ninjarmm/event-health')).data); }
    catch { setHealth({state:'unavailable', counts:null}); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const readiness = health?.readiness;
  const proof = readiness?.canary;
  const runtime = readiness?.runtime;
  const counts = health?.counts;
  return <Card data-testid="ninja-event-health">
    <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
      <CardTitle>Event ingestion &amp; verified Remedy</CardTitle>
      <Button variant="outline" size="sm" disabled={loading} onClick={load}>Refresh events</Button>
    </CardHeader>
    <CardContent className="space-y-5 text-sm">
      <p role="status">{loading ? 'Checking…' : health?.state === 'unavailable' ? 'Status unavailable — refresh to retry' :
        proof?.state === 'verified' ? 'Preview canary verified: script success, independent outcome and live webhook delivery' : 'Preview canary ' + label(proof?.state)}</p>
      <p>Current ingestion health: {label(health?.state)}. Verified canary evidence is historical; processing and scheduling states below are current.</p>
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div><dt>Canary verified</dt><dd>{stamp(proof?.verified_at)}</dd></div>
        <div><dt>Provider script result</dt><dd>{label(proof?.provider_result)}</dd></div>
        <div><dt>Verified live receipts</dt><dd>{proof?.receipt_count ?? '—'}</dd></div>
        <div><dt>Replay deduplication</dt><dd>{label(proof?.replay)}</dd></div>
        <div><dt>Current delegated authorization</dt><dd>{label(readiness?.connection?.state)}</dd></div>
        <div><dt>Authorization expires</dt><dd>{stamp(readiness?.connection?.expires_at)}</dd></div>
        <div><dt>Receiver</dt><dd>{flag(runtime?.receiver_enabled)}</dd></div>
        <div><dt>Event processor</dt><dd>{flag(runtime?.worker_enabled)}</dd></div>
        <div><dt>Queue recovery test</dt><dd>{label(readiness?.checks?.find(check => check.id === 'worker_recovery')?.state)}</dd></div>
        <div><dt>Remedy execution</dt><dd>{flag(runtime?.execution_enabled)}</dd></div>
        <div><dt>Automatic event tickets</dt><dd>{flag(runtime?.tickets_enabled)}</dd></div>
        <div><dt>Last received</dt><dd>{stamp(health?.last_received)}</dd></div>
        <div><dt>Last processed</dt><dd>{stamp(health?.last_processed)}</dd></div>
        <div><dt>Activities catch-up</dt><dd>{label(health?.catchup)}</dd></div>
        <div><dt>Activities cursor test</dt><dd>{label(readiness?.checks?.find(check => check.id === 'activities_cursor_canary')?.state)}</dd></div>
        {['ready','processing','retry','dead_lettered','quarantined'].map(key => <div key={key}>
          <dt>{({ready:'Queued',processing:'Processing',retry:'Retrying',dead_lettered:'Dead letter',quarantined:'Awaiting mapping'})[key]}</dt>
          <dd>{Number.isInteger(counts?.[key]) ? counts[key] : '—'}</dd>
        </div>)}
      </dl>
      {proof?.state === 'verified' && <p>Verified for device {proof.device_id} in this integration. Other event families and continuous delivery coverage need separate validation.</p>}
      {proof?.asset_id && <a className="underline" href={'/assets/' + encodeURIComponent(proof.asset_id)}>Open verified device</a>}
      <div className="rounded-lg border p-4 space-y-2">
        <strong>Production release: {readiness?.production_ready === true ? 'Ready' : readiness ? 'Blocked by remaining gates' : 'Unknown'}</strong>
        {readiness?.release_blockers?.length > 0 && <ul className="list-disc pl-5 space-y-1">{readiness.release_blockers.map(item => <li key={item}>{label(item)}</li>)}</ul>}
      </div>
      <p className="text-muted-foreground">A verified canary does not enable the processor, endpoint execution or automatic tickets. Asset polling status appears separately below.</p>
    </CardContent>
  </Card>;
}
